import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';
import {readLocalStorage} from 'utils/storage/local_storage';

import {Grid2 as Grid, Stack} from 'views/components';

import FilterBar from './scene/filter/FilterBar';
import Header from './scene/Header';
import List from './scene/list/List';
import {generateFilterOptions} from './scene/filter/FilterGenerator';

import backend from 'utils/backend';
import moment from 'moment';

const filterOptions = generateFilterOptions();

const defaultSelection = () => {
  return {
    order: {orderBy: 'creationTime', orderDirc: 'DESC'},
    filter: {
      status: {
        filterData: filterOptions['status'],
        optionSelected: 'ALL',
      },
    },
  };
};

const ClientLeadList = (props) => {
  const [reload, setReload] = React.useState(0);
  const [lastUpdatedAt, setLastUpdatedAt] = React.useState(new Date());
  const [visibleLeads, setVisibleLeads] = React.useState([]);
  const [lastEvaluatedKey, setLastEvaluatedKey] = React.useState();
  const [stage, setStage] = React.useState('FILL');
  const [selection, setSelection] = React.useState(sessionStorage.getItem('CLIENT_FILTER') ?
                                                   JSON.parse(sessionStorage.getItem('CLIENT_FILTER')) : defaultSelection());

  const progressWrap = props.statusProgressWrap;

  const requestIdRef = React.useRef(0);

  const isListLoading = stage === 'LOADING';

  React.useEffect(() => {
    sessionStorage.setItem('CLIENT_FILTER', JSON.stringify(selection));
  }, [selection]);

  const handleItemUpdate = React.useCallback((clientRn, nextItem) => {
    setVisibleLeads((currentData) => {
      return currentData.map((dataItem) => {
        if (dataItem.clientRn === clientRn) {
          return {
            ...dataItem,
            ...nextItem,
          };
        }

        return dataItem;
      });
    });
  }, []);

  const loadData = React.useCallback(async (reqId = requestIdRef.current, startKey = lastEvaluatedKey) => {
    setStage('LOADING');

    await progressWrap(async () => {
      const filter = [];

      for (const filterSelected of Object.values(selection.filter)) {
        if (!filterSelected.optionSelected || filterSelected.optionSelected === 'ALL') {
          continue;
        }

        if (Array.isArray(filterSelected.optionSelected) && filterSelected.optionSelected.length === 0) {
          continue;
        }

        const rawValue = filterSelected.optionSelected;
        const value = filterSelected.filterData.type === 'text' && typeof rawValue === 'string' ?
          rawValue.replace(/[^a-z0-9 ]/gi, '') : rawValue;
        filter.push(
            {
              key: filterSelected.filterData.key,
              value: value,
            },
        );
      }

      const sortBy = {
        sortKey: 'createdAt',
        sortDirection: 'DESC',
      };

      const result = await backend.client.queryClient(filter, sortBy, startKey);

      if (reqId !== requestIdRef.current) return;

      let filteredList = result.list;

      // Filter out ACTIVE and INACTIVE when status is ALL
      const statusFilter = Object.values(selection.filter).find((e) => e.filterData.key === 'status');
      if (statusFilter && (statusFilter.optionSelected === 'ALL' || !statusFilter.optionSelected)) {
        filteredList = filteredList.filter((item) => item.status !== 'ACTIVE' && item.status !== 'INACTIVE');
      }

      setVisibleLeads((e) => {
        return [
          ...e,
          ...filteredList,
        ];
      });
      setLastEvaluatedKey(result.lastEvaluatedKey);
    }, {showProgress: false});

    if (reqId === requestIdRef.current) {
      setStage('COMPLETE');
    }
  }, [lastEvaluatedKey, progressWrap, selection]);

  React.useEffect(() => {
    requestIdRef.current += 1;
    setVisibleLeads([]);
    setLastEvaluatedKey(null);
    loadData(requestIdRef.current, null);
  }, [selection, reload]);

  const lastSeenByClientRn = React.useMemo(() => {
    return readLocalStorage('CLIENT_MESSAGES_LAST_SEEN') || {};
  }, [visibleLeads]);

  const hasUnreadMessages = React.useCallback((clientRn, clientLastMessage) => {
    if (!clientLastMessage || clientLastMessage.sentBy === 'ADMIN') {
      return false;
    }

    const lastSeenTime = lastSeenByClientRn[clientRn];
    return !lastSeenTime || moment(clientLastMessage.createdAt).isAfter(moment(lastSeenTime));
  }, [lastSeenByClientRn]);

  const updateFilter = (filter, option) => {
    setSelection((e) => {
      if (option) {
        return {
          ...e,
          filter: {
            ...e.filter,
            [filter.key]: {
              filterData: filter,
              optionSelected: option.key,
              label: option?.label,
            },
          },
        };
      } else {
        const newFilter = {...e.filter};
        delete newFilter[filter.key];
        return {
          ...e,
          filter: newFilter,
        };
      }
    });
  };

  return (
    <Grid container spacing={1}>
      <Grid size={12}>
        <Header
          lastUpdatedAt={lastUpdatedAt}
          numRefresh={reload}
          setLastUpdatedAt={setLastUpdatedAt}
          setNumRefresh={setReload}
        />
      </Grid>
      <Grid size={12}>
        <Stack spacing={4} width={1} pb={{xs: 0, md: 2}}>
          <FilterBar
            selection={selection}
            filterOptions={filterOptions}
            reload={reload}
            updateFilter={updateFilter}
          />
          <List
            data={visibleLeads}
            hasUnreadMessages={hasUnreadMessages}
            isLoading={isListLoading}
            loadMore={loadData}
            hasMore={!!lastEvaluatedKey}
            onItemUpdate={handleItemUpdate}
            statusProgressWrap={progressWrap}
          />
        </Stack>
      </Grid>
    </Grid>
  );
};
ClientLeadList.propTypes = {
  statusProgressWrap: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
});

export default connect(mapStateToProps, dispatch)(React.memo(ClientLeadList));
